import type { ChangeEvent, KeyboardEvent, RefObject } from "react";
import { useObservable, observer } from "@legendapp/state/react";
import { memo, useEffect, useRef } from "react";
import { Send, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PromptEnhancer } from "@/components/PromptEnhancer";
import { WaveformAnimation } from "@/components/VoiceInputButton";
import { WhisperVoiceInputButton, type WhisperVoiceInputHandle } from "@/components/WhisperVoiceInputButton";

interface ChatInputProps {
  input: string;
  onInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: (value?: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  isStreaming: boolean;
  isLoadingModels?: boolean;
  /** Show the dictation mic (voice mode "record"). Off hides it; "speech" replaces the composer entirely. */
  voiceInputEnabled?: boolean;
  isRecording: boolean;
  recordingTime: number;
  formatRecordingTime: (seconds: number) => string;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  whisperVoiceRef: RefObject<WhisperVoiceInputHandle | null>;
  submitButtonRef?: RefObject<HTMLButtonElement | null>;
  showPromptEnhancer: boolean;
  setShowPromptEnhancer: (open: boolean) => void;
  onTranscript: (finalText: string) => void;
  onInterimTranscript: (text: string) => void;
  onRecordingChange: (recording: boolean) => void;
  onRecordingTimeChange: (time: number) => void;
  onEnhancedPrompt: (prompt: string) => void;
  onSendEnhancedPrompt: (prompt: string) => void;
}

export const ChatInput = memo(observer(function ChatInput({
  input,
  onInputChange,
  onSend,
  onKeyDown,
  isStreaming,
  isLoadingModels = false,
  voiceInputEnabled = true,
  isRecording,
  recordingTime,
  formatRecordingTime,
  textareaRef,
  whisperVoiceRef,
  submitButtonRef,
  showPromptEnhancer,
  setShowPromptEnhancer,
  onTranscript,
  onInterimTranscript,
  onRecordingChange,
  onRecordingTimeChange,
  onEnhancedPrompt,
  onSendEnhancedPrompt,
}: ChatInputProps) {
  const draft$ = useObservable(input);
  const internalSubmitButtonRef = useRef<HTMLButtonElement>(null);
  const buttonRef = submitButtonRef || internalSubmitButtonRef;

  useEffect(() => {
    draft$.set(input);
  }, [input, draft$]);

  return (
    <div className="flex flex-col">
      {isRecording && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-red-500/5">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <div
                className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-30"
                style={{ width: 32, height: 32 }}
              />
              <Button
                type="button"
                variant="default"
                size="icon"
                onClick={() => whisperVoiceRef.current?.stop?.()}
                disabled={isStreaming}
                className="relative z-10 bg-red-500 hover:bg-red-600 text-white h-8 w-8"
                data-testid="button-whisper-voice-input-stop"
                aria-label="Stop recording"
              >
                <Square className="h-3 w-3 fill-current" />
              </Button>
            </div>
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse shrink-0" />
            <WaveformAnimation />
            <span className="text-xs font-medium text-red-600 dark:text-red-400">
              Recording... speak now
            </span>
          </div>
          <span className="text-xs font-medium text-red-600 dark:text-red-400 tabular-nums shrink-0">
            {formatRecordingTime(recordingTime)}
          </span>
        </div>
      )}

      <div className="relative flex items-end gap-2 pt-1 px-2 pb-2">
        <Textarea
          ref={textareaRef}
          value={draft$.get()}
          onChange={(e) => {
            draft$.set(e.target.value);
            onInputChange(e);
          }}
          onKeyDown={onKeyDown}
          placeholder={isRecording ? "Speak now... text will appear as you talk" : "Ask anything..."}
          className="min-h-[44px] max-h-[30vh] resize-none border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:border-0 focus-visible:shadow-none py-2.5 px-3 pr-10 flex-1"
          style={{ boxShadow: "none" }}
          data-testid="input-chat-message"
          disabled={isStreaming}
        />

        <div className="absolute right-3 bottom-3 flex items-center gap-1">
          {voiceInputEnabled && (
            <WhisperVoiceInputButton
              ref={whisperVoiceRef}
              onTranscript={onTranscript}
              onInterimTranscript={onInterimTranscript}
              onRecordingChange={onRecordingChange}
              onRecordingTimeChange={onRecordingTimeChange}
              disabled={isStreaming}
            />
          )}
          <Button
            ref={buttonRef}
            onClick={() => onSend(draft$.get())}
            disabled={!draft$.get().trim() || isStreaming || isLoadingModels}
            size="icon"
            className="h-8 w-8"
            data-testid="button-send-message"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {showPromptEnhancer && draft$.get().trim() && (
        <PromptEnhancer
          prompt={draft$.get()}
          onEnhanced={onEnhancedPrompt}
          onSendEnhanced={onSendEnhancedPrompt}
          onClose={() => setShowPromptEnhancer(false)}
        />
      )}
    </div>
  );
}));
